import autobind from "autobind-decorator";
import { observer } from "mobx-react";
import React from "react";
import { Link } from "react-router";
import styled from "styled-components";

import customerImage from "@client/components/ui/sharedImages/customer.svg";
import licenceImage from "@client/components/ui/sharedImages/licence.svg";
import userImage from "@client/components/ui/sharedImages/licensor.svg";
import partnerImage from "@client/components/ui/sharedImages/partner.svg";
import Style from "@client/utils/Style";

import DropdownButton from "../DropdownButton/DropdownButton";
import { TokenInfo } from "@typings/Identity";
import { isLocationAvailable } from "@client/utils/Constants";

const Wrapper = styled.div`
  position: relative;
`;
const List = styled.div`
  cursor: pointer;
  background: ${Style.palette.grayscale[1]};
  border-radius: ${Style.borderRadius};
  width: 240px;
  position: absolute;
  right: 0;
  top: 45px;
  z-index: 10;
`;
const ListItem = styled(Link)`
  display: flex;
  align-items: center;
  border-bottom: 1px solid white;
  transition: all ${Style.animations.swift};
  text-decoration: none;
  color: ${Style.palette.black};
  &:hover {
    background: ${Style.palette.grayscale[0]};
  }
  &:last-child {
    border-bottom-left-radius: ${Style.borderRadius};
    border-bottom-right-radius: ${Style.borderRadius};
  }
  &:first-child {
    position: relative;
    border-top-left-radius: ${Style.borderRadius};
    border-top-right-radius: ${Style.borderRadius};
    &:after {
      content: " ";
      position: absolute;
      width: 10px;
      height: 10px;
      background: ${Style.palette.grayscale[1]};
      border: 1px solid ${Style.palette.grayscale[1]};
      border-color: transparent transparent ${Style.palette.grayscale[1]}
        ${Style.palette.grayscale[1]};
      transform: rotate(135deg);
      right: 10px;
      top: -6px;
      transition: all ${Style.animations.swift};
    }
    &:hover:after {
      background: ${Style.palette.grayscale[0]};
      border: 1px solid ${Style.palette.grayscale[0]};
      border-color: transparent transparent ${Style.palette.grayscale[0]}
        ${Style.palette.grayscale[0]};
    }
  }
`;

const getIcon = (props: { value: string }) => {
  switch (props.value) {
    case "customer":
      return customerImage;
    case "licence":
      return licenceImage;
    case "user":
      return userImage;
    case "partner":
      return partnerImage;
    default:
      return "";
  }
};

const Icon = styled.div<{ value: string }>`
  min-width: 54px;
  height: 54px;
  background: url("${props => getIcon(props)}") no-repeat center;
  background-size: contain;
  margin: 16px;
`;
const Content = styled.div`
  padding-right: 16px;
`;
const Title = styled.div`
  font-size: 16px;
  margin-bottom: 8px;
`;
const Description = styled.div`
  font-size: 12px;
  color: ${Style.palette.grayscale[4]};
`;

type Props = {
  onChange: (item: string) => void;
  tokenInfo: TokenInfo | null;
};
type State = {
  showDropdownList: boolean;
};
@observer
class DropdownNewItem extends React.Component<Props, State> {
  state: State = {
    showDropdownList: false,
  };

  itemMouseDown = false;

  componentDidMount() {
    window.addEventListener("mousedown", this.handlePageClick, false);
  }

  componentWillUnmount() {
    window.removeEventListener("mousedown", this.handlePageClick, false);
  }

  @autobind
  handlePageClick() {
    if (!this.itemMouseDown) {
      this.setState({ showDropdownList: false });
    }
  }

  handleButtonClick() {
    this.setState({ showDropdownList: !this.state.showDropdownList });
  }

  handleItemClick(item: string) {
    this.setState({ showDropdownList: false });

    if (this.props.onChange) {
      this.props.onChange(item);
    }
  }

  renderList() {
    if (!this.state.showDropdownList) {
      return null;
    }

    const items = [
      {
        title: "Licence",
        value: "licence",
        description: "Create a new Coriolis® License",
      },
      {
        title: "Customer",
        value: "customer",
        description: "Create a new Coriolis® Licence customer",
      },
    ];

    if (isLocationAvailable("/licensors", this.props.tokenInfo)) {
      items.push({
        title: "Licence Manager",
        value: "user",
        description: "Create a new Coriolis® Licence Manager",
      });
    }

    if (isLocationAvailable("/partners", this.props.tokenInfo)) {
      items.push({
        title: "Partner",
        value: "partner",
        description: "Create a new Coriolis® Partner",
      });
    }

    const list = (
      <List>
        {items.map(item => {
          return (
            <ListItem
              key={item.title}
              onMouseDown={() => {
                this.itemMouseDown = true;
              }}
              onMouseUp={() => {
                this.itemMouseDown = false;
              }}
              to="#"
              onClick={() => {
                this.handleItemClick(item.value);
              }}
            >
              <Icon value={item.value} />
              <Content>
                <Title>{item.title}</Title>
                <Description>{item.description}</Description>
              </Content>
            </ListItem>
          );
        })}
      </List>
    );

    return list;
  }

  render() {
    return (
      <Wrapper>
        <DropdownButton
          onMouseDown={() => {
            this.itemMouseDown = true;
          }}
          onMouseUp={() => {
            this.itemMouseDown = false;
          }}
          onClick={() => this.handleButtonClick()}
          value="New"
          primary
          centered
        />
        {this.renderList()}
      </Wrapper>
    );
  }
}

export default DropdownNewItem;
